/**
 * Vimeo Player Component
 * 
 * A reusable Vimeo video player with custom controls using the official Vimeo Player SDK.
 * 
 * Features:
 * - Play/Pause toggle
 * - Progress bar with seek functionality
 * - Mute/Unmute toggle
 * - Closed captions toggle
 * - Fullscreen toggle
 * - Responsive design
 * - Touch-friendly controls
 * 
 * Usage:
 * const player = new VimeoPlayerComponent(containerElement, {
 *   vimeoId: '123456789',
 *   autoplay: false,
 *   muted: false,
 *   loop: false
 * });
 * 
 * Or via data attributes:
 * <div class="vimeo-player" 
 *      data-vimeo-player 
 *      data-vimeo-id="123456789"
 *      data-autoplay="true"
 *      data-muted="true">
 * </div>
 */

(function() {
  'use strict';

  // Vimeo SDK loading promise
  let vimeoSDKPromise = null;

  /**
   * Load the Vimeo Player SDK
   * @returns {Promise} Resolves when SDK is loaded
   */
  function loadVimeoSDK() {
    if (vimeoSDKPromise) return vimeoSDKPromise;
    
    if (window.Vimeo && window.Vimeo.Player) {
      return Promise.resolve();
    }

    vimeoSDKPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://player.vimeo.com/api/player.js';
      script.async = true;
      script.onload = () => {
        console.log('✅ Vimeo SDK loaded');
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Failed to load Vimeo SDK'));
      };
      document.head.appendChild(script);
    });

    return vimeoSDKPromise;
  }

  /**
   * Format time in seconds to MM:SS
   * @param {number} seconds 
   * @returns {string}
   */
  function formatTime(seconds) {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * VimeoPlayerComponent Class
   */
  class VimeoPlayerComponent {
    constructor(container, options = {}) {
      this.container = container;
      this.options = {
        vimeoId: options.vimeoId || container.dataset.vimeoId,
        autoplay: options.autoplay ?? container.dataset.autoplay === 'true',
        muted: options.muted ?? container.dataset.muted === 'true',
        loop: options.loop ?? container.dataset.loop === 'true',
        showControls: options.showControls ?? container.dataset.showControls !== 'false',
        posterUrl: options.posterUrl || container.dataset.posterUrl || null,
        fillContainer: options.fillContainer ?? container.dataset.fillContainer === 'true',
        ...options
      };

      if (!this.options.vimeoId) {
        console.error('VimeoPlayerComponent: No Vimeo ID provided');
        return;
      }

      // State
      this.player = null;
      this.isPlaying = false;
      this.isMuted = this.options.muted;
      this.isCaptionsOn = false;
      this.isFullscreen = false;
      this.duration = 0;
      this.currentTime = 0;
      this.isLoaded = false;
      this.isSeeking = false;
      this.textTracks = [];

      // Elements
      this.elements = {};

      // Bind methods
      this.handlePlay = this.handlePlay.bind(this);
      this.handlePause = this.handlePause.bind(this);
      this.handleTimeUpdate = this.handleTimeUpdate.bind(this);
      this.handleLoaded = this.handleLoaded.bind(this);
      this.handleFullscreenChange = this.handleFullscreenChange.bind(this);

      this.init();
    }

    /**
     * Initialize the player
     */
    async init() {
      try {
        this.buildUI();
        await loadVimeoSDK();
        await this.initPlayer();
        this.bindEvents();
        this.container.classList.add('vimeo-player--initialized');
        console.log('✅ VimeoPlayerComponent initialized:', this.options.vimeoId);
      } catch (error) {
        console.error('VimeoPlayerComponent init error:', error);
        this.handleInitError(error);
      }
    }

    /**
     * Handle initialization errors
     */
    handleInitError(error) {
      // Show error state or fallback
      this.container.classList.add('vimeo-player--error');
      
      // Create error message element
      const errorEl = document.createElement('div');
      errorEl.className = 'vimeo-player__error';
      errorEl.innerHTML = `
        <p>Video unavailable</p>
        <button class="vimeo-player__error-retry" data-vimeo-retry>Retry</button>
      `;
      
      if (this.elements.wrapper) {
        this.elements.wrapper.appendChild(errorEl);
        
        // Retry button
        const retryBtn = errorEl.querySelector('[data-vimeo-retry]');
        if (retryBtn) {
          retryBtn.addEventListener('click', () => {
            errorEl.remove();
            this.container.classList.remove('vimeo-player--error');
            this.initPlayer().then(() => {
              this.bindEvents();
              this.container.classList.add('vimeo-player--initialized');
            }).catch(e => this.handleInitError(e));
          });
        }
      }
    }

    /**
     * Build the player UI
     */
    buildUI() {
      this.container.classList.add('vimeo-player');
      
      this.container.innerHTML = `
        <div class="vimeo-player__wrapper">
          <div class="vimeo-player__video-container" data-vimeo-video-container></div>
          
          ${this.options.posterUrl ? `
            <div class="vimeo-player__poster" data-vimeo-poster>
              <img src="${this.options.posterUrl}" alt="Video thumbnail" loading="lazy">
              <button class="vimeo-player__poster-play" data-vimeo-poster-play aria-label="Play video">
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="40" cy="40" r="38" fill="rgba(255,255,255,0.9)" stroke="none"/>
                  <path d="M32 26L56 40L32 54V26Z" fill="#000"/>
                </svg>
              </button>
            </div>
          ` : ''}
          
          ${this.options.showControls ? `
            <div class="vimeo-player__controls" data-vimeo-controls>
              <button class="vimeo-player__btn vimeo-player__btn--play" data-vimeo-play aria-label="Play">
                <svg class="icon-play" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M8 5v14l11-7L8 5z" fill="currentColor"/>
                </svg>
                <svg class="icon-pause" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" fill="currentColor"/>
                </svg>
              </button>
              
              <div class="vimeo-player__progress" data-vimeo-progress>
                <div class="vimeo-player__progress-bar">
                  <div class="vimeo-player__progress-buffered" data-vimeo-buffered></div>
                  <div class="vimeo-player__progress-played" data-vimeo-played></div>
                </div>
                <input type="range" 
                       class="vimeo-player__progress-input" 
                       data-vimeo-seek 
                       min="0" 
                       max="100" 
                       value="0" 
                       step="0.1"
                       aria-label="Seek">
              </div>
              
              <span class="vimeo-player__time" data-vimeo-time>0:00 / 0:00</span>
              
              <button class="vimeo-player__btn vimeo-player__btn--mute" data-vimeo-mute aria-label="Mute">
                <svg class="icon-volume" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" fill="currentColor"/>
                </svg>
                <svg class="icon-muted" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" fill="currentColor"/>
                </svg>
              </button>
              
              <button class="vimeo-player__btn vimeo-player__btn--cc" data-vimeo-cc aria-label="Closed captions" hidden>
                <svg class="icon-cc-off" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M19 4H5c-1.11 0-2 .9-2 2v12c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 7H9.5v-.5h-2v3h2V13H11v1c0 .55-.45 1-1 1H7c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1zm7 0h-1.5v-.5h-2v3h2V13H18v1c0 .55-.45 1-1 1h-3c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1z" fill="currentColor"/>
                </svg>
                <svg class="icon-cc-on" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M19 4H5c-1.11 0-2 .9-2 2v12c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 7H9.5v-.5h-2v3h2V13H11v1c0 .55-.45 1-1 1H7c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1zm7 0h-1.5v-.5h-2v3h2V13H18v1c0 .55-.45 1-1 1h-3c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1z" fill="currentColor"/>
                  <rect x="3" y="19" width="18" height="2" fill="currentColor"/>
                </svg>
              </button>
              
              <button class="vimeo-player__btn vimeo-player__btn--fullscreen" data-vimeo-fullscreen aria-label="Fullscreen">
                <svg class="icon-expand" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" fill="currentColor"/>
                </svg>
                <svg class="icon-compress" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" fill="currentColor"/>
                </svg>
              </button>
            </div>
          ` : ''}
        </div>
      `;

      // Cache elements
      this.elements = {
        wrapper: this.container.querySelector('.vimeo-player__wrapper'),
        videoContainer: this.container.querySelector('[data-vimeo-video-container]'),
        poster: this.container.querySelector('[data-vimeo-poster]'),
        posterPlay: this.container.querySelector('[data-vimeo-poster-play]'),
        controls: this.container.querySelector('[data-vimeo-controls]'),
        playBtn: this.container.querySelector('[data-vimeo-play]'),
        progress: this.container.querySelector('[data-vimeo-progress]'),
        progressPlayed: this.container.querySelector('[data-vimeo-played]'),
        progressBuffered: this.container.querySelector('[data-vimeo-buffered]'),
        seekInput: this.container.querySelector('[data-vimeo-seek]'),
        time: this.container.querySelector('[data-vimeo-time]'),
        muteBtn: this.container.querySelector('[data-vimeo-mute]'),
        ccBtn: this.container.querySelector('[data-vimeo-cc]'),
        fullscreenBtn: this.container.querySelector('[data-vimeo-fullscreen]')
      };
    }

    /**
     * Initialize the Vimeo Player
     */
    async initPlayer() {
      const playerOptions = {
        id: this.options.vimeoId,
        autopause: true,
        autoplay: this.options.autoplay && !this.options.posterUrl,
        background: false,
        byline: false,
        color: 'ffffff',
        controls: false,
        dnt: true, // Do Not Track - helps reduce Vimeo UI elements
        loop: this.options.loop,
        muted: this.options.muted,
        pip: false,
        playsinline: true,
        portrait: false,
        responsive: !this.options.fillContainer, // Disable responsive if fillContainer is true
        speed: false,
        title: false,
        transparent: true,
        // Hide social/interaction buttons
        like: false,
        watchlater: false,
        share: false,
        vimeo_logo: false
      };
      
      // Add fillContainer class for CSS styling
      if (this.options.fillContainer) {
        this.container.classList.add('vimeo-player--fill-container');
      }

      this.player = new Vimeo.Player(this.elements.videoContainer, playerOptions);

      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Player initialization timeout')), 15000);
      });

      // Wait for player to be ready with timeout
      try {
        await Promise.race([this.player.ready(), timeoutPromise]);
      } catch (error) {
        console.error('Player ready failed:', error);
        throw error;
      }
      
      // Get duration with error handling
      try {
        this.duration = await this.player.getDuration();
      } catch (e) {
        console.warn('Could not get duration:', e);
        this.duration = 0;
      }
      
      // Get text tracks with error handling
      try {
        this.textTracks = await this.player.getTextTracks();
      } catch (e) {
        console.warn('Could not get text tracks:', e);
        this.textTracks = [];
      }
      
      // Show CC button if tracks available
      if (this.textTracks.length > 0 && this.elements.ccBtn) {
        this.elements.ccBtn.removeAttribute('hidden');
      }
      
      // Make iframe not focusable since we use custom controls
      const iframe = this.elements.videoContainer.querySelector('iframe');
      if (iframe) {
        iframe.setAttribute('tabindex', '-1');
        
        // If fillContainer mode, calculate and apply proper scaling
        if (this.options.fillContainer) {
          this.applyFillContainerScaling(iframe);
        }
      }
      
      this.isLoaded = true;
      
      // Sync mute state with actual player state (mobile browsers may override)
      try {
        const actualMuted = await this.player.getMuted();
        const actualVolume = await this.player.getVolume();
        this.isMuted = actualMuted || actualVolume === 0;
        this.container.classList.toggle('vimeo-player--muted', this.isMuted);
      } catch (e) {
        console.warn('Could not sync mute state:', e);
      }
      
      // Update time display
      this.updateTimeDisplay();
    }

    /**
     * Bind event listeners
     */
    bindEvents() {
      if (!this.player) {
        console.error('Cannot bind events: player not initialized');
        return;
      }
      
      // Player events
      this.player.on('play', this.handlePlay);
      this.player.on('pause', this.handlePause);
      this.player.on('timeupdate', this.handleTimeUpdate);
      this.player.on('loaded', this.handleLoaded);
      this.player.on('bufferstart', () => this.showLoading());
      this.player.on('bufferend', () => this.hideLoading());
      
      // Handle player errors
      this.player.on('error', (error) => {
        console.error('Vimeo player error:', error);
        this.hideLoading();
      });

      // Poster click
      if (this.elements.posterPlay) {
        this.elements.posterPlay.addEventListener('click', (e) => {
          e.stopPropagation();
          this.play();
        });
      }
      if (this.elements.poster) {
        this.elements.poster.addEventListener('click', (e) => {
          e.stopPropagation();
          this.play();
        });
      }

      // Play/pause button
      if (this.elements.playBtn) {
        this.elements.playBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.togglePlay();
        });
      }

      // Click on video wrapper to toggle play (only when not on controls)
      // Track if this was a drag or a click
      if (this.elements.wrapper) {
        let startX = 0;
        let startY = 0;
        let isDrag = false;
        
        this.elements.wrapper.addEventListener('mousedown', (e) => {
          startX = e.clientX;
          startY = e.clientY;
          isDrag = false;
        });
        
        this.elements.wrapper.addEventListener('mousemove', (e) => {
          // If mouse moved more than 10px, consider it a drag
          if (Math.abs(e.clientX - startX) > 10 || Math.abs(e.clientY - startY) > 10) {
            isDrag = true;
          }
        });
        
        this.elements.wrapper.addEventListener('click', (e) => {
          // If it was a drag, don't toggle play and let the event bubble for slider
          if (isDrag) {
            isDrag = false;
            return;
          }
          
          // Stop propagation only for actual clicks (not drags)
          e.stopPropagation();
          
          // Only toggle if click is on the video area, not controls
          if (!e.target.closest('[data-vimeo-controls]') && 
              !e.target.closest('[data-vimeo-poster-play]')) {
            this.togglePlay();
          }
        });
      }

      // Seek input
      if (this.elements.seekInput) {
        // Track if video was playing before seeking started
        let wasPlayingBeforeSeek = false;
        let seekTimeout = null;
        
        // Handle mousedown/touchstart to pause and set seeking state immediately
        const handleSeekStart = async () => {
          this.isSeeking = true;
          wasPlayingBeforeSeek = this.isPlaying;
          
          // Pause video while seeking for better UX
          if (this.isPlaying) {
            try {
              await this.player.pause();
            } catch (e) {
              console.warn('Could not pause during seek:', e);
            }
          }
          
          // Add seeking class for visual feedback
          this.container.classList.add('vimeo-player--seeking');
        };
        
        this.elements.seekInput.addEventListener('mousedown', (e) => {
          e.stopPropagation();
          handleSeekStart();
        });
        this.elements.seekInput.addEventListener('touchstart', (e) => {
          e.stopPropagation();
          handleSeekStart();
        }, { passive: false });
        
        this.elements.seekInput.addEventListener('input', (e) => {
          e.stopPropagation();
          const percent = parseFloat(e.target.value);
          this.updateProgressUI(percent);
          
          // Update time display to show selected time
          const seekTime = (percent / 100) * this.duration;
          this.updateTimeDisplayWithValue(seekTime);
        });

        this.elements.seekInput.addEventListener('change', async (e) => {
          e.stopPropagation();
          const percent = parseFloat(e.target.value);
          const time = (percent / 100) * this.duration;
          
          // Clear any pending throttled seeks
          clearTimeout(seekTimeout);
          
          // Show loading state
          this.showLoading();
          
          try {
            await this.player.setCurrentTime(time);
            
            // Wait a moment for buffer to load at new position
            // Listen for the seeked event or use a small delay
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Resume playback if video was playing before seeking
            if (wasPlayingBeforeSeek) {
              await this.player.play();
            }
          } catch (e) {
            console.warn('Seek error:', e);
          } finally {
            this.hideLoading();
            this.isSeeking = false;
            this.container.classList.remove('vimeo-player--seeking');
          }
        });
      }

      // Mute button
      if (this.elements.muteBtn) {
        this.elements.muteBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleMute();
        });
      }

      // CC button
      if (this.elements.ccBtn) {
        this.elements.ccBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleCaptions();
        });
      }

      // Fullscreen button
      if (this.elements.fullscreenBtn) {
        this.elements.fullscreenBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleFullscreen();
        });
      }

      // Fullscreen change event
      document.addEventListener('fullscreenchange', this.handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);

      // Keyboard shortcuts when focused
      this.container.addEventListener('keydown', (e) => {
        e.stopPropagation();
        switch (e.key) {
          case ' ':
          case 'k':
            e.preventDefault();
            this.togglePlay();
            break;
          case 'm':
            e.preventDefault();
            this.toggleMute();
            break;
          case 'f':
            e.preventDefault();
            this.toggleFullscreen();
            break;
          case 'c':
            e.preventDefault();
            this.toggleCaptions();
            break;
          case 'ArrowLeft':
            e.preventDefault();
            this.seek(-10);
            break;
          case 'ArrowRight':
            e.preventDefault();
            this.seek(10);
            break;
        }
      });
      
      // Prevent mousedown/touchstart from bubbling on controls to disable parent slider dragging
      // Also dispatch custom events so parent components can react
      if (this.elements.controls) {
        const dispatchControlStart = () => {
          this.container.dispatchEvent(new CustomEvent('vimeoPlayer:controlStart', {
            bubbles: true,
            detail: { player: this }
          }));
        };
        
        const dispatchControlEnd = () => {
          this.container.dispatchEvent(new CustomEvent('vimeoPlayer:controlEnd', {
            bubbles: true,
            detail: { player: this }
          }));
        };
        
        // Track if we're currently interacting with controls
        let isInteracting = false;
        
        // Pointer events (used by modern browsers and Splide)
        this.elements.controls.addEventListener('pointerdown', (e) => {
          e.stopPropagation();
          isInteracting = true;
          dispatchControlStart();
        });
        
        this.elements.controls.addEventListener('pointermove', (e) => {
          if (isInteracting) {
            e.stopPropagation();
          }
        });
        
        this.elements.controls.addEventListener('pointerup', (e) => {
          e.stopPropagation();
          isInteracting = false;
          dispatchControlEnd();
        });
        
        this.elements.controls.addEventListener('pointercancel', (e) => {
          isInteracting = false;
          dispatchControlEnd();
        });
        
        this.elements.controls.addEventListener('pointerleave', (e) => {
          if (isInteracting) {
            isInteracting = false;
            dispatchControlEnd();
          }
        });
        
        // Mouse events (fallback)
        this.elements.controls.addEventListener('mousedown', (e) => {
          e.stopPropagation();
          e.preventDefault();
          isInteracting = true;
          dispatchControlStart();
        });
        
        this.elements.controls.addEventListener('mouseup', (e) => {
          isInteracting = false;
          dispatchControlEnd();
        });
        
        this.elements.controls.addEventListener('mouseleave', (e) => {
          // Only dispatch end if mouse button is not pressed
          if (e.buttons === 0 && isInteracting) {
            isInteracting = false;
            dispatchControlEnd();
          }
        });
        
        // Touch events (fallback for older mobile browsers)
        this.elements.controls.addEventListener('touchstart', (e) => {
          e.stopPropagation();
          // Don't prevent default on buttons (allows click), but do on progress bar
          if (e.target.closest('.vimeo-player__progress')) {
            e.preventDefault();
          }
          isInteracting = true;
          dispatchControlStart();
        }, { passive: false });
        
        // Prevent touchmove from propagating while interacting with controls
        this.elements.controls.addEventListener('touchmove', (e) => {
          e.stopPropagation();
          // Prevent default to stop any scrolling/dragging behavior on parent
          if (isInteracting) {
            e.preventDefault();
          }
        }, { passive: false });
        
        this.elements.controls.addEventListener('touchend', (e) => {
          e.stopPropagation();
          isInteracting = false;
          dispatchControlEnd();
        }, { passive: false });
        
        this.elements.controls.addEventListener('touchcancel', (e) => {
          isInteracting = false;
          dispatchControlEnd();
        }, { passive: true });
      }

      // Show controls on mouse move, hide after delay
      let controlsTimeout;
      const showControls = () => {
        this.container.classList.add('vimeo-player--show-controls');
        clearTimeout(controlsTimeout);
        if (this.isPlaying) {
          controlsTimeout = setTimeout(() => {
            this.container.classList.remove('vimeo-player--show-controls');
          }, 3000);
        }
      };

      this.container.addEventListener('mousemove', showControls);
      this.container.addEventListener('touchstart', showControls);
      this.container.addEventListener('mouseenter', showControls);
      this.container.addEventListener('mouseleave', () => {
        if (this.isPlaying) {
          this.container.classList.remove('vimeo-player--show-controls');
        }
      });
    }

    /**
     * Handle play event
     */
    async handlePlay() {
      this.isPlaying = true;
      this.container.classList.add('vimeo-player--playing');
      this.container.classList.remove('vimeo-player--paused');
      
      // Hide poster
      if (this.elements.poster) {
        this.elements.poster.classList.add('is-hidden');
      }
      
      // Sync mute state with actual player state (important for mobile)
      // Mobile browsers may force mute, so we need to reflect actual state
      try {
        const actualMuted = await this.player.getMuted();
        const actualVolume = await this.player.getVolume();
        this.isMuted = actualMuted || actualVolume === 0;
        this.container.classList.toggle('vimeo-player--muted', this.isMuted);
      } catch (e) {
        // Ignore if we can't get mute state
      }
      
      // Dispatch custom event
      this.container.dispatchEvent(new CustomEvent('vimeoPlayer:play', {
        bubbles: true,
        detail: { player: this }
      }));
    }

    /**
     * Handle pause event
     */
    handlePause() {
      this.isPlaying = false;
      this.container.classList.remove('vimeo-player--playing');
      this.container.classList.add('vimeo-player--paused');
      this.container.classList.add('vimeo-player--show-controls');
      
      // Dispatch custom event
      this.container.dispatchEvent(new CustomEvent('vimeoPlayer:pause', {
        bubbles: true,
        detail: { player: this }
      }));
    }

    /**
     * Handle time update
     */
    handleTimeUpdate(data) {
      if (this.isSeeking) return;
      
      this.currentTime = data.seconds;
      const percent = (data.seconds / this.duration) * 100;
      
      this.updateProgressUI(percent);
      this.updateTimeDisplay();
    }

    /**
     * Handle loaded event
     */
    async handleLoaded() {
      try {
        this.duration = await this.player.getDuration();
        this.updateTimeDisplay();
      } catch (e) {
        console.warn('Could not get duration on loaded:', e);
      }
    }

    /**
     * Handle fullscreen change
     */
    handleFullscreenChange() {
      const isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
      this.isFullscreen = isFullscreen;
      this.container.classList.toggle('vimeo-player--fullscreen', isFullscreen);
    }

    /**
     * Update progress UI
     */
    updateProgressUI(percent) {
      if (this.elements.progressPlayed) {
        this.elements.progressPlayed.style.width = `${percent}%`;
      }
      if (this.elements.seekInput) {
        this.elements.seekInput.value = percent;
      }
    }

    /**
     * Update time display
     */
    updateTimeDisplay() {
      if (this.elements.time) {
        this.elements.time.textContent = `${formatTime(this.currentTime)} / ${formatTime(this.duration)}`;
      }
    }

    /**
     * Update time display with a specific value (used during seeking)
     */
    updateTimeDisplayWithValue(timeValue) {
      if (this.elements.time) {
        this.elements.time.textContent = `${formatTime(timeValue)} / ${formatTime(this.duration)}`;
      }
    }

    /**
     * Show loading indicator
     */
    showLoading() {
      this.container.classList.add('vimeo-player--loading');
    }

    /**
     * Hide loading indicator
     */
    hideLoading() {
      this.container.classList.remove('vimeo-player--loading');
    }

    /**
     * Play the video
     */
    async play() {
      if (!this.player || !this.isLoaded) {
        console.warn('VimeoPlayer: Cannot play - player not ready');
        return;
      }
      try {
        await this.player.play();
      } catch (error) {
        console.warn('VimeoPlayer play error:', error);
      }
    }

    /**
     * Pause the video
     */
    async pause() {
      if (!this.player) return;
      try {
        await this.player.pause();
      } catch (error) {
        console.warn('VimeoPlayer pause error:', error);
      }
    }

    /**
     * Toggle play/pause
     */
    togglePlay() {
      if (!this.isLoaded) {
        console.warn('VimeoPlayer: Cannot toggle - player not loaded');
        return;
      }
      if (this.isPlaying) {
        this.pause();
      } else {
        this.play();
      }
    }

    /**
     * Toggle mute
     * Handles mobile browser restrictions where unmuting may require user gesture
     * and volume control instead of just mute toggle
     */
    async toggleMute() {
      if (!this.player) return;
      try {
        // Get current actual state from player (may differ from our tracked state on mobile)
        const currentMuted = await this.player.getMuted();
        const currentVolume = await this.player.getVolume();
        
        if (currentMuted || currentVolume === 0) {
          // Currently muted - try to unmute
          // On mobile, we need to set both muted AND volume
          await this.player.setMuted(false);
          // If volume was 0, set it to a reasonable level
          if (currentVolume === 0) {
            await this.player.setVolume(1);
          }
          this.isMuted = false;
        } else {
          // Currently unmuted - mute it
          await this.player.setMuted(true);
          this.isMuted = true;
        }
        
        // Verify the state was actually changed (mobile may block unmute)
        const newMuted = await this.player.getMuted();
        const newVolume = await this.player.getVolume();
        this.isMuted = newMuted || newVolume === 0;
        
        this.container.classList.toggle('vimeo-player--muted', this.isMuted);
        
        // Log for debugging mobile issues
        console.log('VimeoPlayer mute toggle:', { 
          requestedMute: !currentMuted, 
          actualMuted: newMuted, 
          volume: newVolume 
        });
      } catch (error) {
        console.warn('VimeoPlayer mute error:', error);
        // Try to sync UI with actual player state on error
        try {
          const actualMuted = await this.player.getMuted();
          this.isMuted = actualMuted;
          this.container.classList.toggle('vimeo-player--muted', this.isMuted);
        } catch (e) {
          // Ignore secondary error
        }
      }
    }

    /**
     * Toggle captions
     */
    async toggleCaptions() {
      if (!this.player) return;
      try {
        if (this.textTracks.length === 0) return;
        
        this.isCaptionsOn = !this.isCaptionsOn;
        
        if (this.isCaptionsOn) {
          // Enable first available track
          await this.player.enableTextTrack(this.textTracks[0].language);
        } else {
          await this.player.disableTextTrack();
        }
        
        this.container.classList.toggle('vimeo-player--captions-on', this.isCaptionsOn);
      } catch (error) {
        console.warn('VimeoPlayer captions error:', error);
      }
    }

    /**
     * Toggle fullscreen
     */
    async toggleFullscreen() {
      try {
        if (this.isFullscreen) {
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            await document.webkitExitFullscreen();
          }
        } else {
          const elem = this.container;
          if (elem.requestFullscreen) {
            await elem.requestFullscreen();
          } else if (elem.webkitRequestFullscreen) {
            await elem.webkitRequestFullscreen();
          }
        }
      } catch (error) {
        console.warn('VimeoPlayer fullscreen error:', error);
      }
    }

    /**
     * Apply scaling to make video fill container (object-fit: cover behavior)
     * @param {HTMLIFrameElement} iframe - The Vimeo iframe element
     */
    applyFillContainerScaling(iframe) {
      // Default video aspect ratio (most Vimeo videos are 16:9)
      const videoAspectRatio = 16 / 9;
      
      const updateScale = () => {
        const containerWidth = this.container.offsetWidth;
        const containerHeight = this.container.offsetHeight;
        
        if (!containerWidth || !containerHeight) return;
        
        const containerAspectRatio = containerWidth / containerHeight;
        
        let iframeWidth, iframeHeight;
        
        if (containerAspectRatio > videoAspectRatio) {
          // Container is wider than video - scale based on width
          iframeWidth = containerWidth;
          iframeHeight = containerWidth / videoAspectRatio;
        } else {
          // Container is taller than video - scale based on height
          iframeHeight = containerHeight;
          iframeWidth = containerHeight * videoAspectRatio;
        }
        
        iframe.style.width = `${iframeWidth}px`;
        iframe.style.height = `${iframeHeight}px`;
      };
      
      // Initial scale
      updateScale();
      
      // Update on resize
      this.resizeObserver = new ResizeObserver(() => {
        updateScale();
      });
      this.resizeObserver.observe(this.container);
    }

    /**
     * Seek relative to current time
     */
    async seek(seconds) {
      if (!this.player) return;
      try {
        const newTime = Math.max(0, Math.min(this.duration, this.currentTime + seconds));
        await this.player.setCurrentTime(newTime);
      } catch (error) {
        console.warn('VimeoPlayer seek error:', error);
      }
    }

    /**
     * Seek to absolute time
     */
    async seekTo(seconds) {
      if (!this.player) return;
      try {
        const newTime = Math.max(0, Math.min(this.duration, seconds));
        await this.player.setCurrentTime(newTime);
      } catch (error) {
        console.warn('VimeoPlayer seekTo error:', error);
      }
    }

    /**
     * Get current playback time
     */
    getCurrentTime() {
      return this.currentTime || 0;
    }

    /**
     * Check if video is currently playing
     */
    getIsPlaying() {
      return this.isPlaying;
    }

    /**
     * Set volume (0-1)
     */
    async setVolume(volume) {
      if (!this.player) return;
      try {
        await this.player.setVolume(Math.max(0, Math.min(1, volume)));
        this.isMuted = volume === 0;
        this.container.classList.toggle('vimeo-player--muted', this.isMuted);
      } catch (error) {
        console.warn('VimeoPlayer volume error:', error);
      }
    }

    /**
     * Destroy the player
     */
    destroy() {
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
      
      // Clean up ResizeObserver if it exists
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      
      if (this.player) {
        try {
          this.player.destroy();
        } catch (e) {
          console.warn('Error destroying Vimeo player:', e);
        }
        this.player = null;
      }
      
      this.isLoaded = false;
      this.container.innerHTML = '';
      this.container.classList.remove('vimeo-player--initialized', 'vimeo-player--error');
    }
  }

  /**
   * Auto-initialize players with data-vimeo-player attribute
   */
  function initVimeoPlayers() {
    const players = document.querySelectorAll('[data-vimeo-player]:not(.vimeo-player--initialized)');
    players.forEach(container => {
      new VimeoPlayerComponent(container);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVimeoPlayers);
  } else {
    initVimeoPlayers();
  }

  // Expose globally
  window.VimeoPlayerComponent = VimeoPlayerComponent;
  window.initVimeoPlayers = initVimeoPlayers;

})();
